/**
 * ReceiptContracts — desktop port of BigxBookApp/src/printing/contracts/ReceiptContracts.ts.
 */

import { PaperWidth } from './PrintTypes';

export interface ReceiptLineItem {
  name: string;
  qty: number;
  /**
   * Unit of measure ("kg", "pcs", "ltr").
   *
   * Rule 46(f) of the CGST Rules requires quantity AND unit on a tax invoice
   * for goods, so this is a statutory field, not decoration. Optional in the
   * type only because services have no meaningful unit.
   */
  unit?: string;
  rate: number;
  amount: number;
  hsn?: string;
  discountPct?: number;
  /**
   * Value the tax was computed on (net of discount). Rule 46(h)/(i) require the
   * taxable value and the rate per line on a tax invoice; the thermal receipt
   * omits both for want of paper width, so they are optional here and rendered
   * only by the A4 template.
   */
  taxableValue?: number;
  /** GST rate as a percentage — 18 means 18%. */
  gstRate?: number;
  /** CGST+SGST+IGST+CESS charged on this line. */
  taxAmount?: number;
}

export interface ReceiptTaxLine {
  label: string;
  amount: number;
}

/**
 * A party on the invoice. Only the buyer is modelled this way today — the
 * seller's fields are flat on ReceiptData because the thermal header predates
 * this type and reprinting old receipts must not shift.
 */
export interface ReceiptParty {
  name: string;
  gstin?: string;
  /** Single-line, comma-joined address. */
  address?: string;
  stateName?: string;
  /** Two-digit GST state code, e.g. "29". */
  stateCode?: string;
  phone?: string;
}

/**
 * One row of the rate-wise tax summary that Rule 46 expects on a tax invoice:
 * taxable value and tax charged, grouped by HSN and rate.
 */
export interface ReceiptTaxSummaryRow {
  hsn?: string;
  /** GST rate as a percentage — 18 means 18%. */
  gstRate: number;
  cessRate?: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
}

export interface ReceiptPaymentLine {
  method: string;
  paid: number;
  change?: number;
}

/**
 * The seller's own bank, printed so a buyer can pay against the invoice.
 *
 * Note this is the OPPOSITE direction from `Supplier.bankDetails`, which is
 * where the shop sends money. Easy to confuse, and expensive to confuse.
 */
export interface ReceiptBankDetails {
  name?: string;
  accountNo?: string;
  ifsc?: string;
  branch?: string;
}

/**
 * Which of the statutory copies is being printed.
 *
 * Rule 46 of the CGST Rules requires a supply of goods to be invoiced in
 * triplicate, each copy marked with who it is for. A counter sale normally
 * only ever needs the original; a dispatch needs all three.
 */
export type InvoiceCopyType = 'ORIGINAL' | 'DUPLICATE' | 'TRIPLICATE';

export const INVOICE_COPY_LABELS: Record<InvoiceCopyType, string> = {
  ORIGINAL: 'ORIGINAL FOR RECIPIENT',
  DUPLICATE: 'DUPLICATE FOR TRANSPORTER',
  TRIPLICATE: 'TRIPLICATE FOR SUPPLIER',
};

export interface ReceiptData {
  storeName: string;
  addressLine?: string;
  gstin?: string;
  phone?: string;
  /** Seller's state — place of supply is read against this to pick CGST+SGST vs IGST. */
  stateName?: string;
  stateCode?: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerName?: string;
  /**
   * Full buyer block. Present whenever the invoice names a customer; the B2B
   * fields inside (GSTIN, address, state) are what make an A4 print a valid
   * tax invoice rather than a receipt with a name on it. `customerName` stays
   * for the thermal path, which prints nothing else about the buyer.
   */
  customer?: ReceiptParty;
  /** State of supply, as a name. Falls back to the seller's state for walk-ins. */
  placeOfSupply?: string;
  placeOfSupplyCode?: string;
  /** True when IGST applies — kept explicit so the template never re-derives it. */
  isInterState?: boolean;
  /** Rule 46(p): a tax invoice must say whether reverse charge applies. */
  reverseCharge?: boolean;
  items: ReceiptLineItem[];
  subtotal: number;
  discount?: number;
  taxes: ReceiptTaxLine[];
  /** Rate-wise tax breakup. Empty for a nil-rated invoice. */
  taxSummary?: ReceiptTaxSummaryRow[];
  /** Signed: positive rounds up, negative rounds down. Omitted when zero. */
  roundOff?: number;
  total: number;
  payment?: ReceiptPaymentLine;
  dueAmount?: number;
  dueDate?: string;
  notes?: string;
  terms?: string;
  footerLine1?: string;
  footerLine2?: string;
  /** Seller's bank, for the "Company's Bank Details" block. */
  bank?: ReceiptBankDetails;
  /** Seller's e-mail, printed under the contact number on A4. */
  email?: string;
  /**
   * Seller's logo. Only ever rendered when it is a data: URI — an A4 template
   * has to stay self-contained, because the PDF window has no network.
   */
  logoUrl?: string;
}

export interface ReceiptProfile {
  paperWidth: PaperWidth;
  charsPerLine: number;
}

export interface ReceiptBuildOutput {
  text: string;
  bytes: Uint8Array;
}
